import { memo } from 'react'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/play'
import { stageLabel } from '@/lib/watch/rules'
import type { PublicProfile } from '@/lib/watch/types'
import { cn } from '@/lib/utils'
import { HostAvatar } from './HostAvatar'
import './watch.css'

export interface StageButtonProps {
  /** The host of the stage on screen. */
  host: PublicProfile
  /** A host is live on the stage while the room is connected: the avatar's live ring. */
  hostLive?: boolean
  /** How many are live on the stage (seats whose participant is in the connected room), for the button's name. */
  onStage: number
  /** The Stages sheet is open (the button's `aria-expanded`). */
  open: boolean
  /** Opens the Stages sheet (the page's, the one the chat's head opens). */
  onOpen(): void
  className?: string
}

/**
 * The stage on screen on a phone, where every tab and the video on its side can see it, when the event has more than
 * one stage: the host's compact avatar (ringed while live), their name and a chevron on Play's white elevated pill. A
 * tap opens the page's Stages sheet, the same list the chat's head opens; it switches nothing by itself. Named as the
 * chat's head is ("Shane’s stage, live, 2 on stage. Change stage").
 */
export const StageButton = memo(function StageButton({ host, hostLive = false, onStage, open, onOpen, className }: StageButtonProps) {
  return (
    <Button
      variant="ghost"
      elevated
      className={cn('watch-stage-button', className)}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={`${stageLabel(host.name, onStage)}. Change stage`}
      onClick={onOpen}
    >
      <HostAvatar host={host} size="compact" live={hostLive} />
      {/* A name can carry direction characters: isolated, so they can't reorder the chevron after it. */}
      <bdi className="watch-stage-button__name">{host.name}</bdi>
      <ChevronDown aria-hidden="true" />
    </Button>
  )
})
